import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { toast } from "@replo/design-system/components/toast/Toast";
import { DesignLibraryColorPresets } from "@v2/features/design-library/DesignLibraryColorPresets";
import { DesignLibraryColors } from "@v2/features/design-library/DesignLibraryColors";
import { DesignLibraryCustomFonts } from "@v2/features/design-library/DesignLibraryCustomFonts";
import { DesignLibraryFontPresets } from "@v2/features/design-library/DesignLibraryFontPresets";
import { DesignLibraryNavigationBlockerModal } from "@v2/features/design-library/DesignLibraryNavigationBlockerModal";
import { DesignLibrarySummary } from "@v2/features/design-library/DesignLibrarySummary";
import { DesignLibraryTypography } from "@v2/features/design-library/DesignLibraryTypography";
import { useDesignLibrary } from "@v2/features/design-library/hooks/useDesignLibrary";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import {
  useEditorDraftStyleLibrary,
  useEditorStoreActions,
} from "@v2/store/editorStore";
import { stylesRouter, stylesUtils } from "@v2/trpc/routes/styles";
import isEqual from "lodash-es/isEqual";
import { X } from "lucide-react";

export default function DesignLibraryPanel() {
  const { setPanelView } = useEditorStoreActions();
  const projectId = useCurrentProjectId();
  const [isDiscardModalOpen, setIsDiscardModalOpen] = React.useState(false);
  const [isPullingStyles, setIsPullingStyles] = React.useState(false);

  const draftStyleLibrary = useEditorDraftStyleLibrary();
  const { setDraftStyleLibrary } = useEditorStoreActions();

  const {
    currentView,
    setCurrentView,
    handleCustomFontsClick,
    handleCustomFontsBack,
  } = useDesignLibrary();

  const { data: styleLibraryData } = stylesRouter.getStyleLibrary.useQuery(
    { projectId: projectId ?? "" },
    { enabled: Boolean(projectId) },
  );
  const { mutateAsync: updateStyleLibrary, isPending } =
    stylesRouter.createOrUpdateStyleLibrary.useMutation();
  const hasUnsavedChanges =
    draftStyleLibrary !== null &&
    !isEqual(draftStyleLibrary, styleLibraryData?.styleLibrary ?? null);

  const styleLibrary =
    draftStyleLibrary ?? styleLibraryData?.styleLibrary ?? null;

  const handleConfirmClose = async () => {
    setIsDiscardModalOpen(false);
    setDraftStyleLibrary(null);
    setPanelView("chat");
  };

  const handleCancelClose = () => {
    setIsDiscardModalOpen(false);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setIsDiscardModalOpen(true);
    } else {
      setPanelView("chat");
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white">
      <div className="flex h-full min-h-0 w-full flex-col md:border md:border-border md:rounded-xl md:shadow-button-selectable">
        {currentView === "summary" && (
          <div className="hidden md:flex items-center justify-between p-3 border-b-0.5 border-border">
            <h2 className="header-h5">Design Library</h2>
            <Button icon variant="tertiary" size="base" onClick={handleClose}>
              <X />
            </Button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto [&>*]:border-0 [&>*]:shadow-none [&>*]:bg-transparent [&>*]:p-0">
          {currentView === "summary" && (
            <DesignLibrarySummary
              styleLibrary={styleLibrary}
              onColorsClick={() => setCurrentView("colors")}
              onTypographyClick={() => setCurrentView("typography")}
              onCustomFontsClick={handleCustomFontsClick}
              onStyleLibraryChange={(styleLibrary) => {
                return setDraftStyleLibrary(styleLibrary);
              }}
              isPullingStyles={isPullingStyles}
              setIsPullingStyles={setIsPullingStyles}
            />
          )}
          {currentView === "colors" && (
            <DesignLibraryColors
              styleLibrary={styleLibrary}
              onBack={() => setCurrentView("summary")}
              onPresetsClick={() => setCurrentView("presets")}
              onStyleLibraryChange={(styleLibrary) => {
                return setDraftStyleLibrary(styleLibrary);
              }}
            />
          )}
          {currentView === "presets" && (
            <DesignLibraryColorPresets
              styleLibrary={styleLibrary}
              onBack={() => setCurrentView("colors")}
              onStyleLibraryChange={(styleLibrary) => {
                return setDraftStyleLibrary(styleLibrary);
              }}
            />
          )}
          {currentView === "typography" && (
            <DesignLibraryTypography
              styleLibrary={styleLibrary}
              onBack={() => setCurrentView("summary")}
              onCustomFontsClick={handleCustomFontsClick}
              onFontPresetsClick={() => setCurrentView("fontPresets")}
              onStyleLibraryChange={(styleLibrary) => {
                return setDraftStyleLibrary(styleLibrary);
              }}
            />
          )}
          {currentView === "fontPresets" && (
            <DesignLibraryFontPresets
              styleLibrary={styleLibrary}
              onBack={() => setCurrentView("typography")}
              onStyleLibraryChange={(styleLibrary) => {
                return setDraftStyleLibrary(styleLibrary);
              }}
            />
          )}
          {currentView === "customFonts" && (
            <DesignLibraryCustomFonts onBack={handleCustomFontsBack} />
          )}
        </div>
        <div className="flex flex-col md:flex-row justify-end gap-3 md:p-4">
          <Button
            variant="secondary"
            size="base"
            onClick={() => setDraftStyleLibrary(null)}
            disabled={!hasUnsavedChanges}
            className="w-full md:w-fit"
          >
            Discard
          </Button>
          <Button
            variant="primary"
            size="base"
            onClick={() => {
              void (async () => {
                if (!projectId) {
                  return;
                }

                await updateStyleLibrary({
                  projectId,
                  styleLibrary: draftStyleLibrary,
                });
                await stylesUtils.getStyleLibrary.invalidate({
                  projectId: projectId,
                });
                setDraftStyleLibrary(null);
                toast.success({
                  title: "Style Library Updated",
                  description: "Your library has been updated successfully",
                });
              })();
            }}
            disabled={!hasUnsavedChanges || isPending}
            loading={isPending}
            className="w-full md:w-fit"
          >
            Save Changes
          </Button>
        </div>
      </div>

      <DesignLibraryNavigationBlockerModal
        isOpen={isDiscardModalOpen}
        onOpenChange={setIsDiscardModalOpen}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </div>
  );
}
